import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Select, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import useUserSession from '../hooks/useUserSession';
import {
	fetchStaticGroupById,
	getRegionsProfile,
	getZonesByRegion,
} from '../service/MySrcmService';
import { useCallApi } from '../hooks/useCallApi';
import { getZonesOfExaminationCandidates } from '../service/ExamModuleService';
import { Zone } from '../shared/ExamModuleTypes';
import { UserRole } from '../shared/VolunteerApplicationServiceTypes';
import { SelectValue } from 'antd/lib/select';
import debounce from 'lodash/debounce';

const { Option } = Select;

interface OptionData {
	value: string;
	label: string;
}
interface RemoteSingleSelectProps {
	onChange?: (value: Array<OptionData>) => void;
	value?: Array<OptionData>;
	functionarySrcmId?: string;
	multiple?: boolean;
	regionId?: number;
	disabled?: boolean;
	getExamZones?: boolean;
	examIdInSnap?: number;
	regionIds?: Array<number>;
	getZoneChange?: boolean;
}

const RemoteSingleSelect: React.FC<RemoteSingleSelectProps> = ({
	onChange,
	value,
	functionarySrcmId,
	multiple = false,
	regionId,
	disabled = false,
	getExamZones,
	examIdInSnap,
	regionIds,
	getZoneChange,
}: RemoteSingleSelectProps) => {
	const [options, setOptions] = useState<OptionData[]>([]);
	const [loading, setLoading] = useState(false);
	const { t } = useTranslation();
	const { loggedInUserRoles, selectedRole } = useUserSession();
	const { callApi } = useCallApi();

	const currentRoleDetails = loggedInUserRoles?.find(
		(item) => item?.role?.code == selectedRole
	);

	const fetchZones = useCallback(
		async (searchValue: string) => {
			setLoading(true);

			if (regionId && currentRoleDetails?.zones?.length && getZoneChange) {
				const userZones = currentRoleDetails?.zones ?? [];
				try {
					const normalized = userZones
						.map((z) => ({
							zoneId: Number(z.zoneId),
							zoneName: z.zoneName,
						}))
						.filter((z) => Number.isFinite(z.zoneId));

					const zoneWithParent = await Promise.all(
						normalized.map(async (zone) => {
							const data = await fetchStaticGroupById(zone.zoneId);

							return {
								...zone,
								parentId: data.parent_id,
							};
						})
					);

					const filteredZones = zoneWithParent.filter(
						(z) => String(z.parentId).trim() === String(regionId).trim()
					);

					const option = filteredZones
						.filter((z) =>
							z.zoneName?.toLowerCase().includes(searchValue.toLowerCase())
						)
						.map((z) => ({
							value: String(z.zoneId),
							label: z.zoneName,
						}));
					setOptions(option);
					return;
				} catch {
					setOptions([]);
					return;
				}
			} else if (getExamZones) {
				try {
					if (!functionarySrcmId || !examIdInSnap) {
						setOptions([]);
						return;
					}

					const examZones = await callApi(
						() =>
							getZonesOfExaminationCandidates({
								functionarySrcmId,
								examId: examIdInSnap,
								regionIds,
							}),
						'errorOccurredWhileFetchingZones'
					);

					const zones: Zone[] = examZones?.zones ?? [];

					const normalized = zones
						.filter(
							(z) =>
								typeof z.zoneId === 'number' || typeof z.zoneId === 'string'
						)
						.filter((z) => typeof z.zoneName === 'string')
						.map((z) => ({
							zoneId:
								typeof z.zoneId === 'string' ? Number(z.zoneId) : z.zoneId,
							zoneName: z.zoneName,
						}))
						.filter((z) => Number.isFinite(z.zoneId));

					const filteredOptions = normalized
						.filter((z) =>
							z?.zoneName
								?.toLowerCase()
								.includes((searchValue ?? '').toLowerCase())
						)
						.map((z) => ({
							value: String(z?.zoneId ?? ''),
							label: z?.zoneName ?? '',
						}));

					setOptions(filteredOptions);
				} catch {
					setOptions([]);
				} finally {
					setLoading(false);
				}
				return;
			}

			try {
				// Admin flow: load regions via profile, then zones by selected region
				if (
					selectedRole === UserRole.SNP_ADMIN ||
					selectedRole === UserRole.EXAM_ADMIN ||
					selectedRole === UserRole.PREWORK_MENTOR
				) {
					const regionsRes = await getRegionsProfile();
					const allowedRegionIds = (regionsRes.results ?? [])
						.map((r) => r.id)
						.filter((id): id is number => typeof id === 'number');

					const validRegionId = Number(regionId);

					if (!validRegionId || !allowedRegionIds.includes(validRegionId)) {
						setOptions([]);
						return;
					}

					const zonesRes = await getZonesByRegion(validRegionId);
					const zoneOptions = (zonesRes.results ?? [])
						.filter(
							(z): z is { id: number; name: string } =>
								typeof z?.id === 'number' && typeof z?.name === 'string'
						)
						.filter((z) =>
							z?.name?.toLowerCase().includes((searchValue ?? '').toLowerCase())
						)
						.map((z) => ({
							value: String(z?.id ?? ''),
							label: z?.name ?? '',
						}));

					setOptions(zoneOptions);
					return;
				}
				// Non-admin flow: load from logged-in user zones/regions
				let finalZones: { zoneId: string; zoneName: string }[] = [];

				if (currentRoleDetails?.zones?.length) {
					finalZones = currentRoleDetails?.zones.map((z) => ({
						zoneId: String(z?.zoneId),
						zoneName: z?.zoneName,
					}));
				}

				if (currentRoleDetails?.regions?.length) {
					const regionZoneResponses = await Promise.all(
						currentRoleDetails?.regions.map((region) =>
							getZonesByRegion(Number(region?.regionId))
						)
					);

					regionZoneResponses.forEach((response) => {
						const regionZones = (response?.results ?? [])
							.filter(
								(z): z is { id: number; name: string } =>
									typeof z?.id === 'number' && typeof z?.name === 'string'
							)
							.map((z) => ({
								zoneId: String(z?.id ?? ''),
								zoneName: z?.name ?? '',
							}));

						finalZones = [...finalZones, ...regionZones];
					});
				}

				const uniqueZones = Array.from(
					new Map(finalZones?.map((z) => [z?.zoneId, z])).values()
				);

				const filteredOptions = (uniqueZones ?? [])
					.filter((zone) =>
						zone?.zoneName
							?.toLowerCase()
							.includes((searchValue ?? '').toLowerCase())
					)
					.map((zone) => ({
						value: zone?.zoneId ?? '',
						label: zone?.zoneName ?? '',
					}));

				setOptions(filteredOptions);
			} catch {
				setOptions([]);
			} finally {
				setLoading(false);
			}
		},
		[
			selectedRole,
			regionId,
			currentRoleDetails?.zones,
			currentRoleDetails?.regions,
			callApi,
			examIdInSnap,
			functionarySrcmId,
			getExamZones,
			regionIds,
			getZoneChange,
		]
	);

	const debouncedFetchZones = useMemo(
		() => debounce(fetchZones, 500),
		[fetchZones]
	);

	const handleSearch = useCallback(
		(searchKey: SelectValue) => {
			void debouncedFetchZones(searchKey as string);
		},
		[debouncedFetchZones]
	);

	const handleFocus = () => {
		if (options.length === 0) {
			void fetchZones('');
		}
	};

	useEffect(() => {
		void fetchZones('');
	}, [fetchZones]);

	return (
		<Select
			showSearch
			disabled={disabled}
			mode={multiple ? 'multiple' : undefined}
			defaultActiveFirstOption={false}
			filterOption={false}
			onSearch={handleSearch}
			onFocus={handleFocus}
			placeholder={t('searchZones')}
			notFoundContent={loading ? <Spin size="small" /> : null}
			onChange={onChange}
			labelInValue
			value={value}
			suffixIcon={null}
			className="font-sans text-sm text-secondary-gray"
		>
			{options.map((option) => (
				// eslint-disable-next-line
				<Option key={option.value} value={option.value}>
					{option.label}
				</Option>
			))}
		</Select>
	);
};

export default RemoteSingleSelect;
